import { Controller } from "@hotwired/stimulus"
import { DirectUpload } from "@rails/activestorage"

// Files reach storage as they land, and the form only carries their signed
// ids: what commits the Dossier is still one atomic POST at the end
// (docs/fitxers.md). The plain multiple file input underneath keeps working
// with no JavaScript at all — this controller takes its files, uploads them
// and empties it, so nothing is ever sent twice.
//
// Above the threshold the per-file rows give way to a single count: eighty-five
// bars are not information. Failures always stay visible, whatever the count.
export default class extends Controller {
  static targets = ["input", "zone", "list", "summary", "template", "fields", "fallbackSubmit"]
  static values = {
    field: String,
    threshold: { type: Number, default: 5 },
    autoSubmit: Boolean,
    multiple: { type: Boolean, default: true },
    maxBytes: Number,
    forbiddenExtensions: Array,
    tooLargeLabel: String,
    forbiddenTypeLabel: String,
    summaryLabel: String,
    readyLabel: String,
    failedLabel: String
  }

  connect() {
    this.uploads = new Set()
    this.form = this.element.closest("form")
    this.submitRequested = false
    this.onSubmit = (event) => this.guardSubmit(event)
    this.form?.addEventListener("submit", this.onSubmit)
    this.onBeforeCache = () => this.reset()
    document.addEventListener("turbo:before-cache", this.onBeforeCache)
    // Its only reader is a browser that never runs this line
    this.fallbackSubmitTargets.forEach((button) => { button.hidden = true })
    this.render()
  }

  disconnect() {
    this.form?.removeEventListener("submit", this.onSubmit)
    document.removeEventListener("turbo:before-cache", this.onBeforeCache)
    this.uploads.forEach((entry) => entry.xhr?.abort())
  }

  // The snapshot is cloned while this controller is still connected, so the
  // rows have to go before it is taken: restored from cache they would be a
  // screen claiming to hold files whose uploads died with the page.
  reset() {
    this.uploads.clear()
    this.listTarget.replaceChildren()
    this.fieldsTarget.replaceChildren()
    delete this.element.dataset.started
    this.render()
  }

  // The picker and the drop zone are the same gesture with two doors
  pick() {
    this.enqueue(this.inputTarget.files)
    // Emptying it is what keeps Active Storage's own submit hook from
    // uploading the same files a second time, and lets you pick one again
    this.inputTarget.value = ""
  }

  dragOver(event) {
    event.preventDefault()
    this.zoneTarget.dataset.dragging = "true"
  }

  dragLeave() {
    delete this.zoneTarget.dataset.dragging
  }

  drop(event) {
    event.preventDefault()
    delete this.zoneTarget.dataset.dragging
    this.enqueue(event.dataTransfer.files)
  }

  enqueue(files) {
    if (files.length === 0) return

    if (!this.multipleValue && this.uploads.size > 0) {
      Array.from(this.uploads).forEach((entry) => {
        this.uploads.delete(entry)
        entry.xhr?.abort()
        entry.field?.remove()
        entry.row.remove()
      })
    }

    Array.from(files).forEach((file) => this.upload(file))
    this.element.dataset.started = "true"
    if (this.autoSubmitValue) this.submitRequested = true
    this.render()
  }

  upload(file) {
    const row = this.buildRow(file)
    const entry = { file, row, state: "uploading", progress: 0 }
    this.uploads.add(entry)
    this.listTarget.append(row)

    // Refused here so nobody waits for the upload of a file the server is
    // going to refuse anyway. The rule itself lives in DossierFileVersion:
    // this is a courtesy, never the gate (docs/fitxers.md).
    const refusal = this.refusalFor(file)
    if (refusal) {
      entry.state = "rejected"
      entry.message = refusal
      return
    }

    this.start(entry)
  }

  refusalFor(file) {
    if (this.hasMaxBytesValue && file.size > this.maxBytesValue) return this.tooLargeLabelValue

    const extension = file.name.split(".").pop().toLowerCase()
    if (this.forbiddenExtensionsValue.includes(extension)) {
      return this.forbiddenTypeLabelValue.replace("%{extension}", extension)
    }

    return null
  }

  start(entry) {
    entry.state = "uploading"
    entry.progress = 0
    entry.row.querySelector("[data-upload-retry]").hidden = true

    const upload = new DirectUpload(entry.file, this.directUploadUrl, {
      directUploadWillStoreFileWithXHR: (xhr) => {
        entry.xhr = xhr
        xhr.upload.addEventListener("progress", (event) => {
          if (!event.lengthComputable) return

          entry.progress = event.loaded / event.total
          this.render()
        })
      }
    })

    upload.create((error, blob) => {
      if (error) {
        // An abort is a cancellation, not a failure: the row is already gone
        if (!this.uploads.has(entry)) return

        entry.state = "failed"
      } else {
        entry.state = "done"
        entry.progress = 1
        entry.field = this.appendField(blob.signed_id)
      }
      this.render()
      this.submitIfReady()
    })
  }

  retry(event) {
    const entry = this.entryFor(event.target)
    // A rejection is not a failure: trying again would refuse it again
    if (entry && entry.state === "failed") this.start(entry)
    this.render()
  }

  // Cancelling and taking one off the list are the same gesture: the blob of
  // a file removed before committing is swept by UnattachedBlobsCleanupJob.
  remove(event) {
    const entry = this.entryFor(event.target)
    if (!entry) return

    this.uploads.delete(entry)
    entry.xhr?.abort()
    entry.field?.remove()
    entry.row.remove()
    if (this.uploads.size === 0) delete this.element.dataset.started
    this.render()
  }

  // Pressing Done with uploads in flight must not commit half a dossier:
  // the screen holds the submission and sends it when the last one lands.
  guardSubmit(event) {
    if (!this.pending) return

    event.preventDefault()
    this.submitRequested = true
    this.element.setAttribute("aria-busy", "true")
  }

  submitIfReady() {
    if (!this.submitRequested || this.pending) return

    this.submitRequested = false
    this.element.removeAttribute("aria-busy")
    // Failures wait for a decision — retry or remove — instead of quietly
    // committing a dossier without them
    if (!this.failed) this.form?.requestSubmit()
  }

  render() {
    const entries = Array.from(this.uploads)
    const collapsed = entries.length > this.thresholdValue

    entries.forEach((entry) => {
      const row = entry.row
      row.hidden = collapsed && this.settled(entry)
      row.querySelector("[data-upload-bar]").style.width = `${Math.round(entry.progress * 100)}%`
      row.querySelector("[data-upload-track]").hidden = entry.state !== "uploading"
      row.querySelector("[data-upload-retry]").hidden = entry.state !== "failed"
      row.querySelector("[data-upload-status]").textContent = this.statusFor(entry)
    })

    // The list only exists while it shows something: collapsed, or empty,
    // its border would be a line across the screen saying nothing
    this.listTarget.hidden = entries.every((entry) => entry.row.hidden)

    const done = entries.filter((entry) => entry.state === "done").length
    this.summaryTarget.hidden = !collapsed
    this.summaryTarget.textContent = (this.pending ? this.summaryLabelValue : this.readyLabelValue)
      .replace("%{done}", done)
      .replace("%{total}", entries.length)
  }

  statusFor(entry) {
    if (entry.state === "rejected") return entry.message
    if (entry.state === "failed") return this.failedLabelValue
    if (entry.state === "done") return this.humanSize(entry.file.size)

    return `${Math.round(entry.progress * 100)}% · ${this.humanSize(entry.file.size)}`
  }

  humanSize(bytes) {
    const units = ["B", "kB", "MB", "GB"]
    let size = bytes
    let unit = 0
    while (size >= 1024 && unit < units.length - 1) {
      size /= 1024
      unit += 1
    }
    return `${unit === 0 ? size : size.toFixed(1)} ${units[unit]}`
  }

  buildRow(file) {
    const row = this.templateTarget.content.firstElementChild.cloneNode(true)
    row.querySelector("[data-upload-name]").textContent = file.name
    return row
  }

  appendField(signedId) {
    const field = document.createElement("input")
    field.type = "hidden"
    field.name = this.fieldValue
    field.value = signedId
    this.fieldsTarget.append(field)
    return field
  }

  entryFor(element) {
    const row = element.closest("[data-upload-row]")
    return Array.from(this.uploads).find((entry) => entry.row === row)
  }

  get pending() {
    return Array.from(this.uploads).some((entry) => entry.state === "uploading")
  }

  // Nothing that needs a decision may be hidden by the summary, and nothing
  // that needs one lets the form send itself
  settled(entry) {
    return entry.state === "uploading" || entry.state === "done"
  }

  get failed() {
    return Array.from(this.uploads).some((entry) => !this.settled(entry))
  }

  get directUploadUrl() {
    return this.inputTarget.dataset.directUploadUrl
  }
}
